import { FaroTextButton } from "@components/common/faro-text-button";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { FontWeights, useToast } from "@flat-ui";
import { useProjectMarkupUpdate } from "@hooks/project-markups/use-project-markup-update";
import { DeleteMarkupDialog } from "@pages/project-details/project-markups/markup-delete-dialog";
import { nounPluralize } from "@utils/data-display";
import { BulkActionEvents, TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";
/** Show delete attachments button and dispatch action where the delete dialog will act accordingly */
export function BulkDeleteMarkupAttachments({
  selectedAttachments,
  projectId,
  setSelectedAttachments
}) {
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    bulkDeleteAttachmentsFromMarkup
  } = useProjectMarkupUpdate({
    projectId
  });
  const {
    openToast
  } = useToast();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    attachments,
    updateAttachments
  } = useMarkupContext();

  /** Delete markup's attachments in bulk */
  async function deleteMarkupAttachmentsInBulk() {
    trackEvent({
      name: BulkActionEvents.bulkDeleteMarkupAttachment,
      props: {
        dataType: TableType.markupAttachments,
        numberOfEntities: selectedAttachments.length
      }
    });
    try {
      setIsDeleting(true);
      const response = await bulkDeleteAttachmentsFromMarkup(selectedAttachments);
      if (response && response.failedMutations.length === 0) {
        openToast({
          title: "Attachments deleted."
        });
      } else {
        openToast({
          title: `Failed to delete ${nounPluralize({
            counter: response.failedMutations.length,
            word: "Attachment"
          })}`,
          variant: "error"
        });
      }

      // Update the attachments of the annotation
      const updatedAttachments = attachments.filter(attachment => !response.successMutations.some(deletion => deletion.elementId === attachment.id));
      updateAttachments(updatedAttachments);
    } catch (error) {
      handleErrorWithToast({
        id: `Delete attachments-${Date.now().toString()}`,
        title: "Failed to delete the annotation's attachments.",
        error
      });
    } finally {
      setIsDeleting(false);
      setShouldShowDialog(false);
      setSelectedAttachments(new Set());
    }
  }
  return <>
      <FaroTextButton onClick={() => setShouldShowDialog(true)} tooltipText="Delete attachments" sx={{
      fontWeight: FontWeights.SemiBold,
      paddingX: "10px",
      paddingY: "6px",
      width: "60px",
      color: "primary.main"
    }}>
        Delete
      </FaroTextButton>

      {shouldShowDialog && <DeleteMarkupDialog dialogContent={{
      title: "Delete attachments?",
      description: "This will remove the selected attachments from your project."
    }} onClose={() => setShouldShowDialog(false)} onConfirm={deleteMarkupAttachmentsInBulk} isDeleting={isDeleting} shouldShowWarning={false} />}
    </>;
}